import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaYoutube, FaShoppingCart, FaExternalLinkAlt } from 'react-icons/fa';
import './Catalog.css';
import adventureQuestImage from '../assets/BannerJuego.webp';
import adventureQuestImage1 from '../assets/Color4.webp';
import adventureQuestImage2 from '../assets/Color3.webp';
import { API_URL } from '../config';
import NewsletterModal from '../components/NewsletterModal';

function Catalog() {
  const [selectedCategory, setSelectedCategory] = useState('video-games');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  let touchStartX = 0;
  let touchStartY = 0;

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/products/categories/${selectedCategory}/products`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (!Array.isArray(data)) {
          throw new Error('Invalid data format received from server');
        }
        
        setProducts(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [selectedCategory]);

  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchStartX - touchEndX;
      const deltaY = touchStartY - touchEndY;

      if (Math.abs(deltaX) > Math.abs(deltaY) && deltaX > 50) {
        window.location.href = '/upcoming';
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const categories = {
    'video-games': "Video Games",
    'board-games': "Board Games",
    'merchandising': "Merchandising",
    'accessories': "Accessories"
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error: {error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <motion.div 
      className="catalog-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="category-tabs">
        {Object.entries(categories).map(([key, value]) => (
          <button
            key={key}
            className={`category-tab ${selectedCategory === key ? 'active' : ''}`}
            onClick={() => setSelectedCategory(key)}
            data-category={key}
          >
            {value}
          </button>
        ))}
      </div>

      <motion.div 
        className="products-grid"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {products.map(product => (
          <motion.div 
            key={product.id}
            className="product-card"
            whileHover={{ y: -5 }}
          >
            <div className="product-image">
              <img 
                src={product.images && product.images[0] ? `${API_URL}/images/${product.images[0].id}` : adventureQuestImage1} 
                alt={product.name} 
                onError={(e) => {
                  e.target.src = adventureQuestImage1;
                }}
              />
            </div>
            <div className="product-info">
              <h3>{product.name}</h3>
              <p className="product-description">{product.description}</p>
              
              <div className="product-actions">
                {selectedCategory === 'video-games' ? (
                  <>
                    <a 
                      href="https://store.steampowered.com/app/3750350/YOIN_The_shadow_of_dawn/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="video-game-button steam"
                    >
                      <FaExternalLinkAlt /> Wishlist on Steam
                    </a>
                    <a 
                      href="https://www.youtube.com/watch?v=fzf-mzKwB4k" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="video-game-button youtube"
                    >
                      <FaYoutube /> Watch Trailer
                    </a>
                  </>
                ) : (
                  <button 
                    className="notify-button"
                    onClick={() => setIsModalOpen(true)}
                  >
                    <FaShoppingCart /> Be the first to know
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      <NewsletterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Be the First to Know"
        buttonText="I Want to Know"
        listType={selectedCategory === 'board-games' ? 'boardgames' : 'products'}
        successMessage="You'll be the first to know!"
      />
    </motion.div>
  );
}

export default Catalog;